<section class="pay-page">

  <header class="pay-header">
    <span class="pay-header__icon"><mat-icon>payments</mat-icon></span>
    <h1>Payments</h1>
  </header>

  <!-- Error banner -->
  <div class="pay-banner pay-banner--info" *ngIf="confirming" role="status">Confirming your payment…</div>
  <div class="pay-banner" *ngIf="error && !processing" role="alert">
    <mat-icon>error_outline</mat-icon>
    <span>{{ error }}</span>
    <button type="button" class="pay-banner__close" (click)="dismissError()" aria-label="Dismiss">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="state state--loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading payment information…</p>
  </div>

  <div *ngIf="!loading" class="pay-grid">

    <!-- ============ LEFT: Basket ============ -->
    <div class="basket">
      <h2 class="block-title">Cart <span class="block-title__count">{{ returns.length }}</span></h2>
      <div class="basket__list">
        <div class="basket__item" *ngFor="let return of returns">
          <div class="basket__cell basket__period">
            <span class="basket__muted">Return</span>
            <span class="basket__strong">{{ return.period }}</span>
          </div>
          <div class="basket__cell">
            <mat-icon class="basket__ico">description</mat-icon>
            <span>{{ return.type }}{{ return.origin === 'SUPPLEMENTAL' ? ' · Supplemental' : '' }}</span>
          </div>
          <div class="basket__cell">
            <mat-icon class="basket__ico">autorenew</mat-icon>
            <span>{{ return.frequency }}</span>
          </div>
          <div class="basket__cell">
            <mat-icon class="basket__ico">home</mat-icon>
            <span>{{ return.propertyCount ?? 0 }}</span>
          </div>
          <div class="basket__cell basket__total">
            <span class="basket__muted">Total</span>
            <span class="basket__strong">{{ return.balanceDue | currency }}</span>
          </div>
          <button type="button" class="basket__remove" (click)="removeReturn(return.id)"
                  aria-label="Remove return">
            <mat-icon>delete_outline</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- ============ RIGHT: Payment methods ============ -->
    <aside class="methods">
      <h2 class="block-title">Payment methods</h2>

      <form [formGroup]="paymentForm" (ngSubmit)="submitPayment()">

        <!-- Zero filing: return saved as $0, nothing to collect -->
        <div class="zero-note" *ngIf="isZeroFiling">
          <mat-icon>verified</mat-icon>
          <p>No payment is due for this return. File it to record the $0 return and receive a receipt.</p>
        </div>

        <ng-container *ngIf="!isZeroFiling">
        <!-- Pay now vs schedule for later. Only the mock processor offers both; with a live
             gateway there is a single choice, so no toggle is drawn at all. -->
        <div class="seg" role="tablist" *ngIf="isMockGateway">
          <button type="button" class="seg__btn" role="tab"
                  [class.is-active]="payTiming === 'NOW'"
                  (click)="setTiming('NOW')">Pay now</button>
          <!-- Card-on-file isn't implemented for the real processor yet (scheduled payments would
               still run on mock vault tokens), so hide the entry point until Catalis card-on-file
               ships. Remove this *ngIf once it does. -->
          <button type="button" class="seg__btn" role="tab" *ngIf="isMockGateway"
                  [class.is-active]="payTiming === 'SCHEDULE'"
                  (click)="setTiming('SCHEDULE')">Schedule for later</button>
        </div>

        <!-- Schedule panel: future date + saved payment method -->
        <div *ngIf="payTiming === 'SCHEDULE'" class="schedule-panel">
          <div class="field">
            <label for="scheduledDate">Payment date</label>
            <input id="scheduledDate" type="date" [min]="minScheduleDate"
                   [(ngModel)]="scheduledDate" [ngModelOptions]="{ standalone: true }" />
          </div>
          <div class="field" *ngIf="savedMethods.length > 0">
            <label for="savedMethod">Saved payment method</label>
            <select id="savedMethod" [(ngModel)]="savedPaymentMethodId" [ngModelOptions]="{ standalone: true }">
              <option *ngFor="let m of savedMethods" [ngValue]="m.id">{{ methodLabel(m) }}</option>
            </select>
          </div>
          <div class="zero-note" *ngIf="savedMethods.length === 0">
            <mat-icon>info</mat-icon>
            <p>You have no saved payment methods yet. Pay now with "Save this payment method" checked, then schedule future payments with it.</p>
          </div>
          <div class="zero-note">
            <mat-icon>schedule</mat-icon>
            <p>The exact amount (including any interest accrued by then) is charged automatically on the selected date. You can cancel from the Scheduled tab any time before.</p>
          </div>
        </div>

        <ng-container *ngIf="payTiming === 'NOW'">
        <!-- Segmented toggle (mock only: with a live gateway the method is picked on its page) -->
        <div class="seg" role="tablist" *ngIf="isMockGateway">
          <button type="button" class="seg__btn" role="tab"
                  [class.is-active]="paymentForm.get('paymentMethod')?.value === 'ACH'"
                  (click)="paymentForm.get('paymentMethod')?.setValue('ACH')">eCheck</button>
          <button type="button" class="seg__btn" role="tab"
                  [class.is-active]="paymentForm.get('paymentMethod')?.value !== 'ACH'"
                  (click)="paymentForm.get('paymentMethod')?.setValue('CREDIT_CARD')">Debit / Credit card</button>
        </div>

        <!-- ============ eCheck panel ============ -->
        <div class="method-panel" *ngIf="paymentForm.get('paymentMethod')?.value === 'ACH'">
          <div class="method-panel__bar">
            <mat-icon>account_balance</mat-icon> eCheck
          </div>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Routing number</mat-label>
            <input matInput formControlName="routingNumber" maxlength="9" inputmode="numeric" autocomplete="off">
            <mat-error>9-digit routing number required</mat-error>
          </mat-form-field>

          <div class="grid-2">
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>Account number</mat-label>
              <input matInput type="password" formControlName="accountNumber" inputmode="numeric" autocomplete="off">
              <mat-error>{{ paymentForm.get('accountNumber')?.hasError('required') ? 'Account number required' : 'Enter 4 to 17 digits' }}</mat-error>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>Confirm account number</mat-label>
              <input matInput type="password" formControlName="confirmAccountNumber" inputmode="numeric" autocomplete="off">
              <mat-error *ngIf="paymentForm.get('confirmAccountNumber')?.touched && !accountNumbersMatch()">
                Account numbers do not match
              </mat-error>
            </mat-form-field>
          </div>

          <div class="field-row">
            <span class="field-row__label">Account type</span>
            <mat-radio-group formControlName="accountType" class="radio-inline">
              <mat-radio-button value="SAVINGS">Savings</mat-radio-button>
              <mat-radio-button value="CHECKING">Checking</mat-radio-button>
            </mat-radio-group>
          </div>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Name on account</mat-label>
            <input matInput formControlName="accountHolderName" autocomplete="name">
            <mat-error>Name on account required</mat-error>
          </mat-form-field>

          <h3 class="sub-head">Billing address</h3>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Street address</mat-label>
            <input matInput formControlName="billingStreet" autocomplete="address-line1">
            <mat-error>Street address required</mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Apt / Unit / Suite</mat-label>
            <input matInput formControlName="billingAptSuite" autocomplete="address-line2">
          </mat-form-field>

          <div class="grid-3">
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>City</mat-label>
              <input matInput formControlName="billingCity" autocomplete="address-level2">
              <mat-error>Required</mat-error>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>State</mat-label>
              <input matInput formControlName="billingState" autocomplete="address-level1">
              <mat-error>Required</mat-error>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>Zip code</mat-label>
              <input matInput formControlName="billingZip" maxlength="10" inputmode="numeric" autocomplete="postal-code">
              <mat-error>Valid zip required</mat-error>
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Country</mat-label>
            <mat-select formControlName="billingCountry">
              <mat-option value="US">United States</mat-option>
              <mat-option value="CA">Canada</mat-option>
            </mat-select>
          </mat-form-field>

          <div class="field-row">
            <span class="field-row__label">This account is</span>
            <mat-radio-group formControlName="accountClass" class="radio-inline">
              <mat-radio-button value="BUSINESS">Business</mat-radio-button>
              <mat-radio-button value="PERSONAL">Personal</mat-radio-button>
            </mat-radio-group>
          </div>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Email address</mat-label>
            <input matInput type="email" formControlName="email" autocomplete="email">
            <mat-error>Valid email required</mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Phone number</mat-label>
            <input matInput type="tel" formControlName="phone" autocomplete="tel">
            <mat-error>Phone number required</mat-error>
          </mat-form-field>

          <div class="field-row">
            <span class="field-row__label">Receipt</span>
            <!-- Text/SMS receipts have no backend delivery channel; email only until one exists. -->
            <mat-radio-group formControlName="receiptMethod" class="radio-inline">
              <mat-radio-button value="EMAIL">Email</mat-radio-button>
            </mat-radio-group>
          </div>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Memo</mat-label>
            <input matInput formControlName="memo">
          </mat-form-field>
        </div>

        <!-- ============ Hosted checkout notice: method is chosen on the gateway's page ============ -->
        <div class="method-panel" *ngIf="isHostedCheckoutCard">
          <div class="method-panel__bar">
            <mat-icon>lock</mat-icon> Secure online payment
          </div>
          <div class="zero-note">
            <mat-icon>info</mat-icon>
            <p class="hosted-note">Click "Continue to secure payment" to complete the payment. You can pay with debit/credit, eCheck or Pay by Bank where convenience fees may apply.</p>
          </div>
          <ul class="hosted-methods" aria-label="Available payment methods">
            <li><mat-icon>credit_card</mat-icon> Card</li>
            <li><mat-icon>account_balance</mat-icon> Pay By Bank</li>
            <li><mat-icon>receipt_long</mat-icon> eCheck</li>
          </ul>
        </div>

        <!-- ============ Card panel ============ -->
        <div class="method-panel" *ngIf="paymentForm.get('paymentMethod')?.value !== 'ACH' && !isHostedCheckoutCard">
          <div class="method-panel__bar">
            <mat-icon>credit_card</mat-icon> Debit / Credit card
          </div>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Cardholder name</mat-label>
            <input matInput formControlName="cardHolderName" autocomplete="cc-name">
            <mat-error>Cardholder name required</mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Card number</mat-label>
            <input matInput formControlName="cardNumber" (input)="formatCardNumber($event)"
                   placeholder="1234 5678 9012 3456" maxlength="19" inputmode="numeric" autocomplete="cc-number">
            <mat-icon matSuffix *ngIf="paymentForm.get('cardType')?.value">credit_card</mat-icon>
            <mat-error>Valid card number required</mat-error>
          </mat-form-field>

          <div class="grid-3">
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>Exp. month</mat-label>
              <mat-select formControlName="cardExpMonth">
                <mat-option *ngFor="let month of [1,2,3,4,5,6,7,8,9,10,11,12]" [value]="month">
                  {{ month | number: '2.0-0' }}
                </mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>Exp. year</mat-label>
              <mat-select formControlName="cardExpYear">
                <mat-option *ngFor="let year of years" [value]="year">{{ year }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>CVV</mat-label>
              <input matInput type="password" formControlName="cvv" maxlength="4" inputmode="numeric" autocomplete="cc-csc">
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
            <mat-label>Billing ZIP code</mat-label>
            <input matInput formControlName="billingZipCode" maxlength="5" inputmode="numeric" autocomplete="postal-code">
            <mat-error>5-digit ZIP required</mat-error>
          </mat-form-field>
        </div>

        <mat-checkbox formControlName="savePaymentMethod" class="save-method" *ngIf="!isHostedCheckoutCard">
          Save this payment method for future use
        </mat-checkbox>
        </ng-container> <!-- /payTiming NOW -->
        </ng-container>

        <!-- Totals -->
        <div class="totals">
          <!-- Itemized charge breakdown: every charge has a visible line -->
          <div class="totals__row totals__row--muted" *ngIf="sumTaxableReceipts > 0">
            <span>Taxable rental receipts</span>
            <span class="totals__val">{{ sumTaxableReceipts | number:'1.2-2' }}</span>
          </div>
          <div class="totals__row">
            <span>Total taxes</span>
            <span class="totals__val">{{ sumTaxes | number:'1.2-2' }}</span>
          </div>
          <div class="totals__row" *ngIf="sumLateFee > 0">
            <span>Late fee</span>
            <span class="totals__val">{{ sumLateFee | number:'1.2-2' }}</span>
          </div>
          <div class="totals__row" *ngIf="sumLateInterest > 0">
            <span>Late interest</span>
            <span class="totals__val">{{ sumLateInterest | number:'1.2-2' }}</span>
          </div>
          <!-- Always listed so the customer sees it (QA 2026-09-30); $0 on late returns. -->
          <div class="totals__row">
            <span>Online collection allowance<ng-container *ngIf="!sumAllowance"> (on-time filing only)</ng-container></span>
            <span class="totals__val">&minus;{{ sumAllowance | number:'1.2-2' }}</span>
          </div>
          <div class="totals__row">
            <span>Subtotal</span>
            <span class="totals__val">{{ subtotal | currency }}</span>
          </div>
          <div class="totals__row" *ngIf="processingFee > 0">
            <span>Processing fee (retained by payment processor)</span>
            <span class="totals__val">{{ processingFee | number:'1.2-2' }}</span>
          </div>
          <div class="totals__row totals__row--muted" *ngIf="processingFee === 0 && (paymentForm.get('paymentMethod')?.value === 'ACH' || isHostedCheckoutCard)">
            <span>A processing fee set and retained by the payment processor will be added at checkout.</span>
          </div>
          <div class="totals__row totals__row--total">
            <span>Total amount due</span>
            <span class="totals__val">{{ total | currency }}</span>
          </div>
          <div class="totals__row">
            <span>Total payment</span>
            <span class="totals__val">{{ totalPayment | currency }}</span>
          </div>
        </div>
      </form>
    </aside>
  </div>

  <!-- Bottom action bar -->
  <div class="pay-actions" *ngIf="!loading">
    <button mat-stroked-button color="primary" type="button" (click)="cancel()" [disabled]="processing">
      <mat-icon>arrow_back</mat-icon> Go back
    </button>
    <button class="process-btn" mat-flat-button type="button" (click)="submitPayment()" [disabled]="processing">
      <mat-spinner *ngIf="processing" diameter="18"></mat-spinner>
      <ng-container *ngIf="!processing">
        <mat-icon>{{ isZeroFiling ? 'receipt_long' : (payTiming === 'SCHEDULE' ? 'event' : (isHostedCheckoutCard ? 'lock' : 'attach_money')) }}</mat-icon>
        {{ isZeroFiling ? 'File return' : (payTiming === 'SCHEDULE' ? 'Schedule payment' : (isHostedCheckoutCard ? 'Continue to secure payment' : 'Process payment')) }}
      </ng-container>
    </button>
  </div>
</section>
